/**
 * CandidatePicker — right panel. Renders the tenancy-filtered candidate
 * tree and stages an `addInclusion` op on pick. Coverage state (dimmed
 * nodes with tooltip) comes from the server's GetCandidatesResponse PLUS
 * pending add-inclusion ops that haven't saved yet, so the UI reflects the
 * operator's in-progress intent instead of the last-saved server state.
 *
 * No client-side resolver work — we only compare node ids to the list of
 * covering inclusions (persisted + staged). "Does this inclusion's subtree
 * cover this other node" is the server's responsibility; we just dim nodes
 * that are the direct target of an inclusion.
 */

import { useMemo, useState } from 'react';
import type {
  CandidateAppNode,
  CandidateOrgNode,
  CandidateProjectNode,
  CandidateVersionNode,
  GetCandidatesResponse,
  InclusionNodeKind,
  PendingOp,
  PoolInclusion,
} from '@interlucent/organization-protocol';
import styles from './CandidatePicker.module.css';

export interface CandidatePickerProps {
  candidates: GetCandidatesResponse;
  inclusions: PoolInclusion[];
  pendingOps: PendingOp[];
  onOp: (op: PendingOp) => void;
}

export function CandidatePicker({ candidates, inclusions, pendingOps, onOp }: CandidatePickerProps) {
  const [search, setSearch] = useState('');

  // Effective coverage set: any node that is the direct target of a
  // persisted inclusion OR a pending addInclusion, minus those in pending
  // removeInclusion ops. This is a surface-level check — the server's
  // `covered` field on each node already reflects persisted state, but
  // adding staged-op awareness here keeps the picker in sync as the
  // operator works.
  const stagedCoverage = useMemo(() => {
    const set = new Set<string>();
    for (const inc of inclusions) {
      set.add(`${inc.nodeKind}:${inc.nodeId}`);
    }
    const pendingRemovals = new Set<string>();
    for (const op of pendingOps) {
      if (op.kind === 'removeInclusion') pendingRemovals.add(op.id);
      else if (op.kind === 'addInclusion') {
        set.add(`${op.payload.nodeKind}:${op.payload.nodeId}`);
      }
    }
    // If the operator stages a remove of a persisted inclusion, its node
    // should reopen in the picker. Walk persisted inclusions and drop
    // those whose id is in pendingRemovals.
    for (const inc of inclusions) {
      if (pendingRemovals.has(inc.id)) set.delete(`${inc.nodeKind}:${inc.nodeId}`);
    }
    return set;
  }, [inclusions, pendingOps]);

  const [expanded, setExpanded] = useState<{ orgs: Set<string>; projects: Set<string>; apps: Set<string> }>(() => ({
    orgs: new Set(),
    projects: new Set(),
    apps: new Set(),
  }));

  const toggleOrg = (id: string) =>
    setExpanded((prev) => {
      const next = { ...prev, orgs: new Set(prev.orgs) };
      next.orgs.has(id) ? next.orgs.delete(id) : next.orgs.add(id);
      return next;
    });
  const toggleProject = (id: string) =>
    setExpanded((prev) => {
      const next = { ...prev, projects: new Set(prev.projects) };
      next.projects.has(id) ? next.projects.delete(id) : next.projects.add(id);
      return next;
    });
  const toggleApp = (id: string) =>
    setExpanded((prev) => {
      const next = { ...prev, apps: new Set(prev.apps) };
      next.apps.has(id) ? next.apps.delete(id) : next.apps.add(id);
      return next;
    });

  // Search filter: haystack across org/project/app names + app slugs + version ids.
  const q = search.trim().toLowerCase();
  const matches = (hay: string) => !q || hay.toLowerCase().includes(q);

  const filteredOrgs = useMemo(() => {
    if (!q) return candidates.orgs;
    return candidates.orgs
      .map((org) => filterOrg(org, matches))
      .filter((o): o is CandidateOrgNode => o !== null);
  }, [candidates.orgs, q]);

  // While searching, auto-expand everything so matches are visible.
  const effectiveExpanded = q
    ? autoExpandAll(filteredOrgs)
    : expanded;

  const counts = useMemo(() => countNodes(candidates.orgs), [candidates.orgs]);

  const handlePick = (
    kind: InclusionNodeKind,
    id: string,
    covered: boolean,
    deleted: boolean,
    _label: string,
  ) => {
    if (covered || deleted) return;
    onOp({
      kind: 'addInclusion',
      payload: {
        nodeKind: kind,
        nodeId: id,
        onlyDefaultVersion: false,
        precacheOverride: null,
        prelaunchOverride: null,
        multiplexOverride: null,
      },
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>Add to pool</div>
        <div className={styles.panelCounts}>
          {counts.orgs}o / {counts.projects}p / {counts.apps}a / {counts.versions}v
        </div>
      </div>
      <div className={styles.searchBar}>
        <input
          type="search"
          className={styles.search}
          placeholder="Search orgs, projects, apps, version ids…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className={styles.tree}>
        {filteredOrgs.length === 0 && (
          <div className={styles.emptyState}>
            {q ? 'No candidates match the search.' : 'No candidates available for this tenancy.'}
          </div>
        )}
        {filteredOrgs.map((org) => {
          const orgOpen = effectiveExpanded.orgs.has(org.id);
          const orgCovered = org.covered !== null || stagedCoverage.has(`org:${org.id}`);
          return (
            <div key={org.id}>
              <TreeRow
                kind="org"
                cls={styles.orgRow}
                open={orgOpen}
                label={org.name || org.id}
                hint={org.id}
                covered={orgCovered}
                deleted={org.deactivatedAt !== null}
                onToggle={() => toggleOrg(org.id)}
                onPick={() => handlePick('org', org.id, orgCovered, org.deactivatedAt !== null, org.name)}
              />
              {orgOpen &&
                org.projects.map((project) => {
                  const projOpen = effectiveExpanded.projects.has(project.id);
                  const projCovered = project.covered !== null || stagedCoverage.has(`project:${project.id}`);
                  return (
                    <div key={project.id}>
                      <TreeRow
                        kind="project"
                        cls={styles.projectRow}
                        open={projOpen}
                        label={project.name || project.id}
                        hint={project.id}
                        covered={projCovered}
                        deleted={project.deletedAt !== null}
                        onToggle={() => toggleProject(project.id)}
                        onPick={() => handlePick('project', project.id, projCovered, project.deletedAt !== null, project.name)}
                      />
                      {projOpen &&
                        project.apps.map((app) => {
                          const appOpen = effectiveExpanded.apps.has(app.id);
                          const appCovered = app.covered !== null || stagedCoverage.has(`app:${app.id}`);
                          return (
                            <div key={app.id}>
                              <TreeRow
                                kind="app"
                                cls={styles.appRow}
                                open={appOpen}
                                label={`${app.name || app.slug}  ·  ${app.slug}`}
                                hint={app.id}
                                covered={appCovered}
                                deleted={app.deletedAt !== null}
                                onToggle={() => toggleApp(app.id)}
                                onPick={() => handlePick('app', app.id, appCovered, app.deletedAt !== null, app.name)}
                              />
                              {appOpen &&
                                app.versions.map((v) => {
                                  const vCovered = v.covered !== null || stagedCoverage.has(`version:${v.id}`);
                                  return (
                                    <TreeRow
                                      key={v.id}
                                      kind="version"
                                      cls={styles.versionRow}
                                      leaf
                                      label={v.id}
                                      hint={(v.gameEngine && v.operatingSystem) ? `${v.gameEngine}/${v.operatingSystem}${v.isLocal ? ' · local' : ''}` : v.isLocal ? 'local' : ''}
                                      covered={vCovered}
                                      deleted={v.deletedAt !== null}
                                      onPick={() => handlePick('version', v.id, vCovered, v.deletedAt !== null, v.id)}
                                    />
                                  );
                                })}
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TreeRow({
  kind,
  cls,
  open,
  leaf,
  label,
  hint,
  covered,
  deleted,
  onToggle,
  onPick,
}: {
  kind: InclusionNodeKind;
  cls: string;
  open?: boolean;
  leaf?: boolean;
  label: string;
  hint?: string;
  covered: boolean;
  deleted: boolean;
  onToggle?: () => void;
  onPick: () => void;
}) {
  const disabled = covered || deleted;
  const title = deleted
    ? 'This node is soft-deleted and cannot be added.'
    : covered
    ? 'Already covered by an inclusion — remove the inclusion first to pick this node directly.'
    : `Add ${kind} "${label}" as a pool inclusion.`;
  const rowClass = [
    styles.row,
    cls,
    covered ? styles.covered : '',
    deleted ? styles.deleted : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={rowClass}>
      {leaf ? (
        <span className={styles.chevronSpacer} />
      ) : (
        <span className={styles.chevron} onClick={onToggle} role="button" aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? '▾' : '▸'}
        </span>
      )}
      <span className={styles.nodeKind}>{kind}</span>
      <span className={styles.label}>{label}</span>
      {hint && <span className={styles.idHint}>{hint}</span>}
      {covered ? (
        <span className={styles.coverage} title={title}>covered</span>
      ) : (
        <button
          type="button"
          className={styles.addOnly}
          onClick={onPick}
          disabled={disabled}
          title={title}
        >
          + add
        </button>
      )}
    </div>
  );
}

function filterOrg(
  org: CandidateOrgNode,
  matches: (s: string) => boolean,
): CandidateOrgNode | null {
  const orgHit = matches(`${org.name} ${org.id}`);
  const projects: CandidateProjectNode[] = [];
  for (const p of org.projects) {
    const proj = filterProject(p, matches);
    if (proj) projects.push(proj);
  }
  if (orgHit || projects.length > 0) {
    return { ...org, projects: orgHit ? org.projects : projects };
  }
  return null;
}

function filterProject(
  project: CandidateProjectNode,
  matches: (s: string) => boolean,
): CandidateProjectNode | null {
  const projHit = matches(`${project.name} ${project.id}`);
  const apps: CandidateAppNode[] = [];
  for (const a of project.apps) {
    const app = filterApp(a, matches);
    if (app) apps.push(app);
  }
  if (projHit || apps.length > 0) {
    return { ...project, apps: projHit ? project.apps : apps };
  }
  return null;
}

function filterApp(
  app: CandidateAppNode,
  matches: (s: string) => boolean,
): CandidateAppNode | null {
  const appHit = matches(`${app.name} ${app.slug} ${app.id}`);
  const versions: CandidateVersionNode[] = [];
  for (const v of app.versions) {
    if (appHit || matches(v.id)) versions.push(v);
  }
  if (appHit || versions.length > 0) {
    return { ...app, versions: appHit ? app.versions : versions };
  }
  return null;
}

function autoExpandAll(orgs: CandidateOrgNode[]): { orgs: Set<string>; projects: Set<string>; apps: Set<string> } {
  const o = new Set<string>();
  const p = new Set<string>();
  const a = new Set<string>();
  for (const org of orgs) {
    o.add(org.id);
    for (const project of org.projects) {
      p.add(project.id);
      for (const app of project.apps) a.add(app.id);
    }
  }
  return { orgs: o, projects: p, apps: a };
}

function countNodes(orgs: CandidateOrgNode[]): { orgs: number; projects: number; apps: number; versions: number } {
  let oc = 0;
  let pc = 0;
  let ac = 0;
  let vc = 0;
  for (const org of orgs) {
    oc++;
    for (const project of org.projects) {
      pc++;
      for (const app of project.apps) {
        ac++;
        vc += app.versions.length;
      }
    }
  }
  return { orgs: oc, projects: pc, apps: ac, versions: vc };
}
